import { useCallback, useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { EMAIL_TEMPLATE_DESIGN_ONLY_TYPES } from "constants/templates";
import { viteEnv } from "utils/vite-env.util";

import { useAppSelector } from "../../app/hooks";
import { selectCurrentUserToken } from "../../features/auth/auth-slice";
import TextTemplatesDialogV2, {
  TemplateSubmitData
} from "../word-processor-field/text-templates/text-templates-v2/text-templates-dialog-v2.component";
import { imagesUploadHandler } from "../word-processor-field/word-processor-field-setup.utils";
import type { EmailBuilderMergeTags } from "./email-builder-merge-tags";

type EmailBuilderComponentProps = {
  emailBuilderRef: React.MutableRefObject<UnlayerEditor | null>;
  visible?: boolean;
  initialMessage?: string;
  initialDesign?: UnlayerDesign | null;
  mergeTags?: EmailBuilderMergeTags;
  specialLinks?: UnlayerSpecialLink[];
  setActiveDesign?: (design: UnlayerDesign | null) => void;
  setEditorTabIsDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Callback when design is updated in the builder.
   * Used to detect changes for enabling save buttons.
   */
  onDesignChange?: () => void;
  /**
   * Template types to filter in the templates dialog.
   * When not defined, uses EMAIL_TEMPLATE_DESIGN_ONLY_TYPES.
   */
  templateTypes?: number[];
  /**
   * Whether this instance is active and should respond to global events.
   * When multiple instances are mounted, only the active one responds to
   * events like opening the templates modal.
   */
  isActive?: boolean;
  /**
   * When true, the builder fills its container (width and height 100%).
   * Used for full-screen mode.
   */
  fillContainer?: boolean;
};

export default function EmailBuilderComponent({
  emailBuilderRef,
  visible = true,
  initialDesign,
  setActiveDesign,
  setEditorTabIsDisabled,
  onDesignChange,
  templateTypes = EMAIL_TEMPLATE_DESIGN_ONLY_TYPES,
  isActive = true,
  mergeTags,
  specialLinks,
  fillContainer = false
}: EmailBuilderComponentProps) {
  const token = useAppSelector(selectCurrentUserToken);
  const [isLoading, setIsLoading] = useState(true);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);
  const fieldIdRef = useRef<string | undefined>("email-builder-templates");

  const containerId = useRef(new Date().getTime().toString()).current;
  // Listen for postMessage from Unlayer iframe to open templates modal
  // Only respond when this instance is active to prevent multiple modals
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "OPEN_TEMPLATES_MODAL" && isActive) {
        setShowTemplatesDialog(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isActive]);

  const handleTemplateSubmit = useCallback(
    (values: TemplateSubmitData) => {
      const { template, needReplace } = values;

      if (!template || !emailBuilderRef.current) {
        setShowTemplatesDialog(false);
        return;
      }

      // If template has email_design_json, use it directly
      if (template.email_design_json) {
        if (needReplace) {
          emailBuilderRef.current.loadDesign(template.email_design_json);
        } else {
          // Append: export current design, merge rows, then load
          emailBuilderRef.current.exportHtml(
            (data: { design: UnlayerDesign }) => {
              const currentDesign = data.design;
              const templateDesign = template.email_design_json;

              if (templateDesign?.body?.rows && currentDesign?.body?.rows) {
                const mergedDesign: UnlayerDesign = {
                  ...currentDesign,
                  body: {
                    ...currentDesign.body,
                    rows: [
                      ...currentDesign.body.rows,
                      ...templateDesign.body.rows
                    ]
                  }
                };
                emailBuilderRef.current?.loadDesign(mergedDesign);
              } else {
                emailBuilderRef.current?.loadDesign(
                  templateDesign as UnlayerDesign
                );
              }
            }
          );
        }
      }
      //  else if (template.text) {
      //   // Convert plain text to Unlayer design structure
      //   const textWithReplacedMergeTags = template.text.replace(
      //     /{{(.*?)}}/g,
      //     "!--SUNO:$1--"
      //   );

      //   const wrappedText = textWithReplacedMergeTags.startsWith("<")
      //     ? textWithReplacedMergeTags
      //     : `<p style="line-height: 140%;">${textWithReplacedMergeTags}</p>`;

      //   const textDesign: UnlayerDesign = {
      //     counters: { u_row: 1, u_column: 1, u_content_text: 1 },
      //     body: {
      //       id: "body-1",
      //       rows: [
      //         {
      //           id: `row-template-${Date.now()}`,
      //           cells: [1],
      //           columns: [
      //             {
      //               id: `col-template-${Date.now()}`,
      //               contents: [
      //                 {
      //                   id: `text-template-${Date.now()}`,
      //                   type: "text",
      //                   values: {
      //                     anchor: "",
      //                     text: stripUnlayerShell(wrappedText),
      //                     _meta: {
      //                       htmlID: "u_content_text_1",
      //                       htmlClassNames: "u_content_text"
      //                     }
      //                   }
      //                 }
      //               ]
      //             }
      //           ]
      //         }
      //       ]
      //     }
      //   };

      //   if (needReplace) {
      //     emailBuilderRef.current.loadDesign(textDesign);
      //   } else {
      //     // Append text design rows to current design
      //     emailBuilderRef.current.exportHtml(
      //       (data: { design: UnlayerDesign }) => {
      //         const currentDesign = data.design;
      //         if (currentDesign?.body?.rows) {
      //           const mergedDesign: UnlayerDesign = {
      //             ...currentDesign,
      //             body: {
      //               ...currentDesign.body,
      //               rows: [...currentDesign.body.rows, ...textDesign.body.rows]
      //             }
      //           };
      //           emailBuilderRef.current?.loadDesign(mergedDesign);
      //         } else {
      //           emailBuilderRef.current?.loadDesign(textDesign);
      //         }
      //       }
      //     );
      //   }
      // }

      setEditorTabIsDisabled?.(true);
      setShowTemplatesDialog(false);
    },
    [emailBuilderRef, setEditorTabIsDisabled]
  );

  useEffect(() => {
    setIsLoading(true);

    const editor = unlayer.createEditor({
      id: containerId,
      projectId: Number(viteEnv.VITE_UNLAYER_PROJECT_ID),
      displayMode: "email",
      appearance: {
        loader: {
          html: "<div></div>",
          css: ""
        }
      },
      features: {
        audit: true,
        headersAndFooters: true,
        ai: {
          copilot: true,
          magicImage: true,
          smartText: true,
          smartButtons: true,
          smartImageAltText: true,
          smartHeadings: true
        },
        stockImages: true,
        userUploads: {
          enabled: false
        }
      },
      customCSS: [
        `.toolbar-group [role="menu"] [role="menuitem"],
        [role="menu"][aria-labelledby] [role="menuitem"] {
          min-height: 30px !important;
          height: auto !important;
          padding-top: 4px !important;
          padding-bottom: 4px !important;
        }`
      ],
      customJS: [
        `
  (function() {
    function injectCustomButton() {
      const navTabs = document.querySelector('ul.nav.nav-tabs');

      if (!navTabs) {
        setTimeout(injectCustomButton, 100);
        return;
      }

      if (document.getElementById('custom-templates-button')) {
        return;
      }

      const li = document.createElement('li');
      li.id = 'custom-templates-button';
      li.className = 'nav-item tab-blocks';
      li.style.marginTop = '15px';

      const a = document.createElement('a');
      a.setAttribute('role', 'button');
      a.className = 'nav-link';

      const iconSpan = document.createElement('span');
      iconSpan.className = 'nav-icon';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1phnduy');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.style.width = '20px';
      svg.style.height = '20px';

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm20.41 8.41-4.83-4.83c-.37-.37-.88-.58-1.41-.58H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.83c0-.53-.21-1.04-.59-1.42M7 7h7v2H7zm10 10H7v-2h10zm0-4H7v-2h10z');

      svg.appendChild(path);
      iconSpan.appendChild(svg);

      const nameSpan = document.createElement('span');
      nameSpan.className = 'nav-name';
      nameSpan.textContent = 'Templates';

      a.appendChild(iconSpan);
      a.appendChild(nameSpan);

      a.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        window.parent.postMessage(
          { type: "OPEN_TEMPLATES_MODAL" },
          "*"
        );
      });

      li.appendChild(a);
      navTabs.insertBefore(li, navTabs.firstChild);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectCustomButton);
    } else {
      injectCustomButton();
    }
  })();
  `
      ],
      mergeTags,
      specialLinks,
      tools: {
        html: {
          enabled: true
        },
        text: {
          enabled: true
        },
        image: {
          enabled: true,
          properties: {
            src: {
              editor: {
                widgetParams: {
                  placeholderUrl: "IMAGE_PLACEHOLDER_URL"
                }
              }
            }
          }
        },
        button: { enabled: true },
        divider: { enabled: true },
        spacer: { enabled: true },
        heading: {
          enabled: true
        },
        video: { enabled: true },
        social: { enabled: true },
        menu: { enabled: true },
        timer: { enabled: true }
      }
    });

    editor?.registerCallback(
      "image",
      async function (
        file: { attachments: File[] },
        done: (result: { progress?: number; url?: string }) => void
      ) {
        try {
          const actualFile = file.attachments?.[0];

          done({ progress: 10 });

          const blobInfo = {
            blob: () => actualFile,
            base64: () => {
              return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result as string;
                  resolve(result.split(",")[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(actualFile);
              });
            }
          };

          const progress = () => {};

          const url = await imagesUploadHandler({
            token,
            autoUpload: true,
            blobInfo,
            progress,
            maxImageSizeMB: 4
          });

          done({ progress: 100, url });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to upload image";
          console.error("Image upload error:", errorMessage);
          done({ progress: 100 });
        }
      }
    );

    editor?.addEventListener("editor:ready", () => {
      setIsLoading(false);
    });

    editor?.addEventListener("design:updated", function (data) {
      if (data.type && data.type !== "design:migrated") {
        setEditorTabIsDisabled?.(true);
        onDesignChange?.();
      }
    });

    emailBuilderRef.current = editor;
    return () => {
      editor?.destroy?.();
      emailBuilderRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailBuilderRef,
    token,
    containerId,
    setEditorTabIsDisabled,
    mergeTags,
    specialLinks
  ]);

  useEffect(() => {
    if (visible && !isLoading && emailBuilderRef.current) {
      if (initialDesign) {
        emailBuilderRef.current.loadDesign(initialDesign);
        setActiveDesign?.(null);
        setEditorTabIsDisabled?.(true);
        return;
      }

      // NOTE: Loading from initialMessage is disabled to keep Email Builder content independent
      // from the Editor. To re-enable, uncomment the code below and add this ref:
      // const hasInitializedFromMessageRef = useRef(false);
      //
      // if (initialMessage && !hasInitializedFromMessageRef.current) {
      //   hasInitializedFromMessageRef.current = true;
      //
      //   const messageWithReplacedMergeTags = initialMessage.replace(
      //     /{{(.*?)}}/g,
      //     "!--SUNO:$1--"
      //   );
      //
      //   const wrappedMessage = messageWithReplacedMergeTags.startsWith("<")
      //     ? messageWithReplacedMergeTags
      //     : `<p style="line-height: 140%;">${messageWithReplacedMergeTags}</p>`;
      //
      //   emailBuilderRef.current.loadDesign({
      //     counters: {
      //       u_row: 1,
      //       u_column: 1,
      //       u_content_text: 1
      //     },
      //     body: {
      //       id: "body-1",
      //       rows: [
      //         {
      //           id: "row-1",
      //           cells: [1],
      //           columns: [
      //             {
      //               id: "col-1",
      //               contents: [
      //                 {
      //                   id: "text-1",
      //                   type: "text",
      //                   values: {
      //                     anchor: "",
      //                     text: stripUnlayerShell(wrappedMessage),
      //                     _meta: {
      //                       htmlID: "u_content_text_1",
      //                       htmlClassNames: "u_content_text"
      //                     }
      //                   }
      //                 }
      //               ]
      //             }
      //           ]
      //         }
      //       ]
      //     }
      //   });
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, emailBuilderRef, visible, setActiveDesign]);

  return (
    <Box
      sx={{
        position: "relative",
        display: visible ? "block" : "none",
        width: "100%",
        ...(fillContainer && {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flex: 1,
          minHeight: 0
        })
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 10,
            gap: 2
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading email builder...
          </Typography>
        </Box>
      )}
      <div
        id={containerId}
        style={{
          ...(fillContainer
            ? {
                flex: 1,
                minHeight: 0,
                width: "100%",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                overflow: "hidden"
              }
            : {
                width: "100%",
                height: `calc(95vh - 500px)`,
                minHeight: `400px`,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden"
              })
        }}
      ></div>

      <TextTemplatesDialogV2
        fieldIdRef={fieldIdRef}
        show={showTemplatesDialog}
        onClose={() => setShowTemplatesDialog(false)}
        templateTypes={templateTypes}
        onSubmit={handleTemplateSubmit}
        disableComposeEmail
      />
    </Box>
  );
}
